import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";

// AppMediaRules - which `data-wac-media` values a contact page may ask the app to load, and where
// each one lives in the folder. No habitat (no DOM, no XHR), so the Bridge suite can import it and
// assert that what the page generator writes is exactly what this accepts (`MediaContract.test.ts`).
//
// ==================================================================================================
// !! ⛔ WHY THE APP HAS TO FETCH MEDIA AT ALL
// ==================================================================================================
//
// The contact page is shown in a `blob:` iframe (`AppMain.Frame` says why: fragment navigation).
// A relative `src="Media/x.jpg"` resolves against a `blob:` URL, which cannot be a base, so the
// browser loads nothing - and even an absolute Dropbox URL would not help, because Dropbox needs the
// read ticket in an `Authorization` HEADER and an `<img>` cannot send one. So the app reads each
// file with the ticket (`AppDropboxSource.LoadMedia`) and hands the element a blob URL of its own.
//
// ⛔ THE PATTERN IS AN ALLOW-LIST, NOT A SANITISER. The value comes out of a page in the subscriber's
// Dropbox, and the app turns it into a Dropbox read with a live ticket. Anything but
// `Media/<id>.<known ext>` is refused outright - no `..`, no second folder, no query, no scheme.
// ⚠ Every extension `MediaRules.ExtensionFor` (Bridge) can produce must be listed here.

export class AppMediaRules {
    /** The attribute the page generator writes on each `<img>` / `<audio>`. */
    public static readonly Attr: string = "data-wac-media";

    /**
     * ⭐ THE CONTACT'S WHATSAPP PROFILE PICTURE, BESIDE THE PAGE (2026-09-27). The service writes it
     * (`AvatarFetchRun` -> `DossierFolderRun.PlaceAvatar`) under exactly this name; the page's header
     * disc carries it as `data-wac-media`, and `index.json` names it on a row that has one. The
     * Bridge imports this constant (`AvatarRules.FileName`), so the two halves cannot drift.
     */
    public static readonly AvatarFile: string = "Avatar.jpg";

    /** ⛔ `Media/<id>.<known ext>`, or EXACTLY `Avatar.jpg` - nothing else. */
    public static readonly Pattern: RegExp = /^(Media\/[A-Za-z0-9_-]{1,128}\.(jpg|png|webp|gif|ogg|opus|wav|mp3|m4a|aac|amr|pdf|txt|csv|zip|docx|xlsx|pptx|doc|xls|ppt)|Avatar\.jpg)$/;

    /** ⭐ (2026-10-14) A document beside the page is a DOWNLOAD: its element carries this attribute and the blob URL
     *  goes into `href`, never `src`. ⛔ Its blob is typed `application/octet-stream` (`TypeFor`), so no browser
     *  renders or runs it inline. */
    public static readonly DocAttr: string = "data-wac-doc";

    /** Extension -> the type the blob is labelled with, so `<audio>` knows what it is playing. */
    private static readonly Types: Record<string, string> = {
        jpg: "image/jpeg", png: "image/png", webp: "image/webp", gif: "image/gif",
        ogg: "audio/ogg", opus: "audio/ogg", wav: "audio/wav", mp3: "audio/mpeg", m4a: "audio/mp4", aac: "audio/aac",
        amr: "audio/amr",
    };

    /**
     * ⭐ THE NAME A DOWNLOADED DOCUMENT IS SAVED UNDER (both halves): the sender's file name with every path,
     * reserved and control character taken out, at most 100 characters, and ALWAYS ending in our own extension (the
     * one the file was kept under), so a "report.pdf.exe" cannot arrive as an executable. Nothing left = "document.<ext>".
     */
    public static SafeDownloadName = (Name: string, Ext: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "document"; }
        try {
            const Want: string = "." + nz(Ext).toLowerCase();
            let Clean: string = nz(Name).replace(new RegExp("[\\\\/:*?\"<>|\\x00-\\x1f\\x7f]", "g"), "").trim();
            Clean = Clean.replace(new RegExp("^[. ]+"), "").replace(new RegExp("[. ]+$"), "");
            if (Clean.toLowerCase().endsWith(Want) === true) { Clean = Clean.substring(0, Clean.length - Want.length); }
            Clean = Array.from(Clean).slice(0, 100 - Want.length).join("").replace(new RegExp("[. ]+$"), "");
            if (Clean.length === 0) { Clean = "document"; }
            return Clean + Want;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaRules.SafeDownloadName");
            return "document";
        }
    };

    /** DOCUMENTED EXCEPTION: a pure test of a value in hand.
     *  ERROR-SUPPRESSED-JUSTIFIED: cannot throw; false IS the answer for anything else. */
    public static Allowed = (Rel: string): boolean => {
        return AppMediaRules.Pattern.test(nz(Rel));
    };

    /**
     * The media file's href in the folder, from the page's own href: `People/Tomo/Tomo.html` +
     * `Media/a.jpg` -> `People/Tomo/Media/a.jpg`. "" when the value is not allowed or the page has
     * no folder. ⚠ The page part stays URI-encoded exactly as `index.json` wrote it, so it goes
     * through the same `DropboxReadRules.PathFor` decoding as the page itself did.
     */
    public static Resolve = (PageHref: string, Rel: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (AppMediaRules.Allowed(Rel) === false) { return ""; }
            const Page: string = nz(PageHref).split("#")[0].split("?")[0];
            const Cut: number = Page.lastIndexOf("/");
            if (Cut <= 0) { return ""; }
            return Page.substring(0, Cut) + "/" + Rel;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaRules.Resolve");
            return "";
        }
    };

    /** The blob type for a media href; "application/octet-stream" for anything unknown. */
    public static TypeFor = (Href: string): string => {
        if (getCoreUtils().KErrorMode === true) { return "application/octet-stream"; }
        try {
            const Dot: number = nz(Href).lastIndexOf(".");
            if (Dot < 0) { return "application/octet-stream"; }
            const Known: string | undefined = AppMediaRules.Types[Href.substring(Dot + 1).toLowerCase()];
            if (Known === undefined) { return "application/octet-stream"; }
            return Known;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppMediaRules.TypeFor");
            return "application/octet-stream";
        }
    };
}
